import { create } from 'zustand';
import { bookingApi, setAuthToken } from './api';
import type { Account, Hold, Service, Site } from './types';

/**
 * Portal state.
 *
 * Two stores rather than one: the session outlives a booking (a customer stays
 * signed in across visits), while the draft is thrown away the moment a booking
 * completes. Keeping them apart means "start another booking" is a one-line
 * reset that cannot accidentally sign anybody out.
 */

const tokenKey = (slug: string): string => `booking:${slug}:token`;

/**
 * Guarded localStorage.
 *
 * Reading it at module scope throws where there is no DOM (tests, any future
 * prerender) and in Safari private mode. Since a missing preference is not an
 * error, every access degrades to "no value" instead.
 */
const storage = {
  get(key: string): string | null {
    try {
      return globalThis.localStorage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      globalThis.localStorage?.setItem(key, value);
    } catch {
      /* quota or private mode — the preference is simply not persisted */
    }
  },
  remove(key: string): void {
    try {
      globalThis.localStorage?.removeItem(key);
    } catch {
      /* nothing to do */
    }
  },
};

interface SessionState {
  token: string | null;
  account: Account | null;
  status: 'idle' | 'loading' | 'ready';
  hydrate: (slug: string) => Promise<void>;
  signIn: (slug: string, token: string, account: Account) => void;
  signOut: (slug: string) => void;
  setAccount: (account: Account) => void;
}

export const useSession = create<SessionState>((set) => ({
  token: null,
  account: null,
  status: 'idle',

  /** Restore a stored token and confirm it is still good. */
  hydrate: async (slug) => {
    const stored = storage.get(tokenKey(slug));
    if (!stored) {
      set({ status: 'ready' });
      return;
    }
    set({ status: 'loading', token: stored });
    setAuthToken(stored);
    try {
      const account = await bookingApi.me(slug);
      set({ account, status: 'ready' });
    } catch {
      // Expired, revoked, or the customer was blocked — start clean.
      storage.remove(tokenKey(slug));
      setAuthToken(null);
      set({ token: null, account: null, status: 'ready' });
    }
  },

  signIn: (slug, token, account) => {
    storage.set(tokenKey(slug), token);
    setAuthToken(token);
    set({ token, account, status: 'ready' });
  },

  signOut: (slug) => {
    storage.remove(tokenKey(slug));
    setAuthToken(null);
    set({ token: null, account: null, status: 'ready' });
  },

  setAccount: (account) => set({ account }),
}));

export type BookingStep = 'branch' | 'services' | 'staff' | 'time' | 'review' | 'done';

interface DraftState {
  branchId: number | null;
  services: Service[];
  employeeId: number | null;
  /** ISO instant of the chosen slot. */
  startAt: string | null;
  localTime: string | null;
  localDate: string | null;
  hold: Hold | null;
  notes: string;

  setBranch: (id: number) => void;
  toggleService: (service: Service, max: number) => void;
  clearServices: () => void;
  setEmployee: (id: number | null) => void;
  setSlot: (startAt: string, localDate: string, localTime: string) => void;
  setHold: (hold: Hold | null) => void;
  setNotes: (notes: string) => void;
  reset: (keepBranch?: boolean) => void;

  totalDuration: () => number;
  totalPrice: () => number;
  serviceIds: () => number[];
}

export const useDraft = create<DraftState>((set, get) => ({
  branchId: null,
  services: [],
  employeeId: null,
  startAt: null,
  localTime: null,
  localDate: null,
  hold: null,
  notes: '',

  setBranch: (id) =>
    set((s) =>
      // Changing location invalidates everything downstream: the service
      // catalogue, the staff list and the schedule are all per-branch.
      s.branchId === id
        ? { branchId: id }
        : { branchId: id, services: [], employeeId: null, startAt: null, localTime: null, localDate: null, hold: null },
    ),

  toggleService: (service, max) =>
    set((s) => {
      const exists = s.services.some((x) => x.item_id === service.item_id);
      const services = exists
        ? s.services.filter((x) => x.item_id !== service.item_id)
        : s.services.length >= max
          ? s.services
          : [...s.services, service];
      // The chosen time was computed for the old duration, so it no longer holds.
      return { services, startAt: null, localTime: null, localDate: null, hold: null };
    }),

  clearServices: () => set({ services: [], startAt: null, localTime: null, localDate: null, hold: null }),

  setEmployee: (id) => set({ employeeId: id, startAt: null, localTime: null, localDate: null, hold: null }),

  setSlot: (startAt, localDate, localTime) => set({ startAt, localDate, localTime, hold: null }),

  setHold: (hold) => set({ hold }),

  setNotes: (notes) => set({ notes }),

  reset: (keepBranch = true) =>
    set((s) => ({
      branchId: keepBranch ? s.branchId : null,
      services: [],
      employeeId: null,
      startAt: null,
      localTime: null,
      localDate: null,
      hold: null,
      notes: '',
    })),

  totalDuration: () => get().services.reduce((sum, s) => sum + s.duration_minutes, 0),
  // Summed raw and rounded only at render, by the tenant's currency precision.
  // Rounding here to 2 would quietly truncate a 3-decimal currency, and the
  // authoritative total is the server's anyway — this is the running preview.
  totalPrice: () => get().services.reduce((sum, s) => sum + s.price, 0),
  serviceIds: () => get().services.map((s) => s.item_id),
}));

interface UiState {
  site: Site | null;
  lang: 'en' | 'ar';
  authOpen: boolean;
  setSite: (site: Site) => void;
  setLang: (lang: 'en' | 'ar') => void;
  setAuthOpen: (open: boolean) => void;
}

export const useUi = create<UiState>((set) => ({
  site: null,
  lang: (storage.get('booking:lang') as 'en' | 'ar' | null) ?? 'en',
  authOpen: false,
  setSite: (site) => set({ site }),
  setLang: (lang) => {
    storage.set('booking:lang', lang);
    set({ lang });
  },
  setAuthOpen: (authOpen) => set({ authOpen }),
}));
